import { useCallback, useMemo, useRef } from 'react';
import { Menu, MenuItem, TextField, Typography } from '@rossum/ui/material';
import {
  getFilteredCommands,
  useSlashCommands,
} from '../hooks/useSlashCommands';

type ChatTextFieldProps = {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  selectedSuggestion: number;
  setSelectedSuggestion: React.Dispatch<React.SetStateAction<number>>;
  onPaste: (event: React.ClipboardEvent) => void;
  placeholder: string;
  disabled: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLInputElement | null>;
};

export const ChatTextField = ({
  value,
  setValue,
  selectedSuggestion,
  setSelectedSuggestion,
  onPaste,
  placeholder,
  disabled,
  inputRef: externalInputRef,
}: ChatTextFieldProps) => {
  const localRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const inputRef = externalInputRef ?? localRef;
  const ref = useRef<HTMLDivElement>(null);
  const { commands } = useSlashCommands({ enabled: !disabled });

  const trimmedStartValue = value.trimStart();
  const showSuggestions =
    !disabled &&
    !value.includes('\n') &&
    !trimmedStartValue.includes(' ') &&
    trimmedStartValue.startsWith('/');

  const applySuggestion = useCallback(
    (commandName: string) => {
      setValue(`${commandName} `);
      setSelectedSuggestion(0);
      inputRef.current?.focus();
    },
    [setValue, setSelectedSuggestion, inputRef]
  );

  const handleValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(event.target.value);
      setSelectedSuggestion(0);
    },
    [setValue, setSelectedSuggestion]
  );

  const filteredCommands = useMemo(
    () =>
      showSuggestions ? getFilteredCommands(commands, trimmedStartValue) : [],
    [commands, showSuggestions, trimmedStartValue]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (showSuggestions && filteredCommands.length > 0) {
        if (event.key === 'Tab') {
          event.preventDefault();
          const selected = filteredCommands[selectedSuggestion];
          if (selected) {
            applySuggestion(selected.name);
          }
          return;
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          const selected = filteredCommands[selectedSuggestion];
          if (selected) {
            applySuggestion(selected.name);
          }
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedSuggestion(prev => Math.max(prev - 1, 0));
          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedSuggestion(prev =>
            Math.min(prev + 1, filteredCommands.length - 1)
          );
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.currentTarget.closest('form')?.requestSubmit();
      }
    },
    [
      applySuggestion,
      filteredCommands,
      selectedSuggestion,
      showSuggestions,
      setSelectedSuggestion,
    ]
  );

  return (
    <>
      <Menu
        open={showSuggestions && filteredCommands.length > 0}
        anchorEl={ref.current}
        onClose={() => setSelectedSuggestion(0)}
        anchorOrigin={{ vertical: -8, horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoFocus={false}
        disableAutoFocus
        disableAutoFocusItem
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        disableScrollLock
        slotProps={{
          root: { sx: { pointerEvents: 'none' } },
          paper: {
            elevation: 1,
            sx: {
              pointerEvents: 'auto',
              maxHeight: 220,
              width: ref.current?.clientWidth,
            },
          },
        }}
      >
        {filteredCommands.map((command, index) => (
          <MenuItem
            key={command.name}
            data-cy="chat-slash-command-item"
            selected={index === selectedSuggestion}
            onMouseDown={event => event.preventDefault()}
            onClick={() => applySuggestion(command.name)}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{ fontFamily: 'monospace', fontWeight: 500 }}
            >
              {command.name}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {command.description}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
      <TextField
        fullWidth
        multiline
        maxRows={20}
        size="small"
        placeholder={placeholder}
        value={value}
        onChange={handleValueChange}
        onKeyDown={handleKeyDown}
        onPaste={onPaste}
        disabled={disabled}
        autoComplete="off"
        ref={ref}
        inputRef={inputRef}
        slotProps={{
          htmlInput: {
            'data-cy': 'chat-message-input',
          },
        }}
      />
    </>
  );
};
